import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { chromaticKeys, isMinorKey, keyOffset } from '@/lib/song';
import { cn } from '@/lib/utils';

/**
 * Seletor de tom: grade 6×2 com os 12 tons (maiores ou menores, conforme a cifra). O tom atual
 * fica em branco, o original com borda azul. Escolher um tom devolve o deslocamento
 * (−5…+6) em relação a `base`, o tom de referência (do repertório ou original da música).
 */
export const KeyPicker: React.FC<{
  /** Tom de referência: transposição 0. */
  base: string;
  /** Tom exibido agora. */
  current: string;
  /** Deslocamento em relação a `base` (−5…+6). */
  onPick: (steps: number) => void;
  /** Texto do botão (ex.: `TOM D +2`). */
  children: React.ReactNode;
  className?: string;
}> = ({ base, current, onPick, children, className }) => {
  const [open, setOpen] = useState(false);
  const minor = isMinorKey(base);
  const sameRoot = (a: string, b: string) => keyOffset(a, b) === 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'h-[34px] min-w-[96px] rounded-[9px] border border-border bg-raised px-3 font-bold text-[12px] text-white uppercase tracking-wider transition-colors hover:border-accent-line',
            className,
          )}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-3" align="center">
        <div className="grid grid-cols-6 gap-1.5">
          {chromaticKeys(minor).map((key) => {
            const isCurrent = sameRoot(key, current);
            const isBase = sameRoot(key, base);

            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  onPick(keyOffset(base, key));
                  setOpen(false);
                }}
                className={cn(
                  'h-9 rounded-[8px] border font-bold text-[13px] transition-colors',
                  isCurrent
                    ? 'border-white bg-white text-black'
                    : isBase
                      ? 'border-accent-ink text-accent-soft hover:bg-raised'
                      : 'border-border text-muted-foreground hover:bg-raised hover:text-white',
                )}
              >
                {key}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => {
            onPick(0);
            setOpen(false);
          }}
          className="mt-3 w-full text-center font-semibold text-[12px] text-accent-soft hover:underline"
        >
          Tom original ({base})
        </button>
      </PopoverContent>
    </Popover>
  );
};
